import { Check } from "lucide-react";

/**
 * VerifyDot — small colour-coded indicator showing how long ago a pantry item
 * was last counted/verified. Hover/title shows full text.
 *
 *  - never verified  → grey ring (✕) with text "Aldri sjekket"
 *  - verified < 7d   → green dot, "for N dager siden"
 *  - 7d ≤ N ≤ threshold → amber dot
 *  - > threshold     → red dot + ring
 *
 *  Also renders a 1-click "Bekreft" hurtigknapp (sjekk-ikon) that calls
 *  onVerify(itemId) when clicked. Verifies without changing quantity.
 */
export default function VerifyDot({ item, thresholdDays = 14, onVerify, verifying = false }) {
  const last = (item?.last_verified_at || "").trim();
  let ageDays = null;
  if (last) {
    try {
      const d = new Date(last + "T00:00:00Z");
      ageDays = Math.floor((Date.now() - d.getTime()) / 86400000);
    } catch { /* ignore */ }
  }

  let color = "bg-stone-300"; // never
  let label = "Aldri sjekket";
  let textLabel = "Aldri talt";
  let textClass = "text-stone-500";
  if (ageDays !== null) {
    if (ageDays === 0) { color = "bg-emerald-500"; label = "Sjekket i dag"; textLabel = "Talt i dag"; textClass = "text-emerald-700"; }
    else if (ageDays < 7) { color = "bg-emerald-500"; label = `Sjekket for ${ageDays} dag${ageDays === 1 ? "" : "er"} siden`; textLabel = `Talt ${ageDays}d siden`; textClass = "text-emerald-700"; }
    else if (ageDays <= thresholdDays) { color = "bg-amber-400"; label = `Sjekket for ${ageDays} dager siden`; textLabel = `Talt ${ageDays}d siden`; textClass = "text-amber-700"; }
    else { color = "bg-red-500"; label = `Sjekket for ${ageDays} dager siden – trenger ny opptelling!`; textLabel = `${ageDays}d siden – tell snart`; textClass = "text-red-600 font-semibold"; }
  }

  const handleVerify = (e) => {
    e.stopPropagation();
    if (verifying || !onVerify) return;
    onVerify(item.id);
  };

  return (
    <span
      className={`inline-flex items-center gap-1.5 text-[11px] ${textClass}`}
      title={label}
      data-testid={`verify-dot-${item.id}`}
    >
      <span className={`inline-block h-2 w-2 rounded-full ${color}`} aria-label={label} />
      <span>{textLabel}</span>
      {onVerify && (
        <button
          type="button"
          onClick={handleVerify}
          disabled={verifying}
          className="ml-1 inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-white px-2 py-0.5 text-[10px] font-semibold text-emerald-700 hover:bg-emerald-50 hover:border-emerald-400 disabled:opacity-40 transition-colors"
          title="Bekreft – marker som talt opp nå (uendret mengde)"
          data-testid={`verify-btn-${item.id}`}
        >
          <Check className="h-3 w-3" />
          Bekreft
        </button>
      )}
    </span>
  );
}
